import { type RawFrameToken } from '@canvas/frame-api-types';

export interface TokenMetadata {
  token: RawFrameToken;
  acquiredAt: number;
  expiresAt: number;
  version: number;
}

export interface TokenCacheKey {
  boardId: string;
  developmentSpaceId: string;
  developmentCloudId: string;
}

const DEFAULT_TOKEN_TTL_MS = 60 * 60 * 1000;

const TOKEN_REFRESH_THRESHOLD = 0.9;

const FAILURE_COOLDOWN_MS = 1000;

interface FailureMetadata {
  failedAt: number;
  error: Error;
}

export class TokenCache {
  private cache = new Map<string, TokenMetadata>();
  private pendingRequests = new Map<string, Promise<RawFrameToken>>();
  private failureCache = new Map<string, FailureMetadata>();

  private getCacheKey(key: TokenCacheKey): string {
    return `${key.developmentCloudId}:${key.developmentSpaceId}:${key.boardId}`;
  }

  private shouldRefreshToken(metadata: TokenMetadata): boolean {
    const ttl = metadata.expiresAt - metadata.acquiredAt;
    const elapsed = Date.now() - metadata.acquiredAt;
    return elapsed > ttl * TOKEN_REFRESH_THRESHOLD;
  }

  get(key: TokenCacheKey): RawFrameToken | null {
    const cacheKey = this.getCacheKey(key);
    const metadata = this.cache.get(cacheKey);

    if (!metadata) {
      return null;
    }

    if (Date.now() >= metadata.expiresAt) {
      this.cache.delete(cacheKey);
      return null;
    }

    if (this.shouldRefreshToken(metadata)) {
      return null;
    }

    return metadata.token;
  }

  set(
    key: TokenCacheKey,
    token: RawFrameToken,
    ttlMs: number = DEFAULT_TOKEN_TTL_MS
  ): void {
    const cacheKey = this.getCacheKey(key);
    const existing = this.cache.get(cacheKey);
    const version = existing ? existing.version + 1 : 1;

    this.cache.set(cacheKey, {
      token,
      acquiredAt: Date.now(),
      expiresAt: Date.now() + ttlMs,
      version,
    });
  }

  getPendingRequest(key: TokenCacheKey): Promise<RawFrameToken> | undefined {
    const cacheKey = this.getCacheKey(key);
    return this.pendingRequests.get(cacheKey);
  }

  setPendingRequest(key: TokenCacheKey, promise: Promise<RawFrameToken>): void {
    const cacheKey = this.getCacheKey(key);
    this.pendingRequests.set(cacheKey, promise);
  }

  clearPendingRequest(key: TokenCacheKey): void {
    const cacheKey = this.getCacheKey(key);
    this.pendingRequests.delete(cacheKey);
  }

  recordFailure(key: TokenCacheKey, error: Error): void {
    const cacheKey = this.getCacheKey(key);
    this.failureCache.set(cacheKey, {
      failedAt: Date.now(),
      error,
    });
  }

  getFailure(key: TokenCacheKey): Error | null {
    const cacheKey = this.getCacheKey(key);
    const failure = this.failureCache.get(cacheKey);

    if (!failure) {
      return null;
    }

    const elapsed = Date.now() - failure.failedAt;
    if (elapsed >= FAILURE_COOLDOWN_MS) {
      this.failureCache.delete(cacheKey);
      return null;
    }

    return failure.error;
  }

  clearFailure(key: TokenCacheKey): void {
    const cacheKey = this.getCacheKey(key);
    this.failureCache.delete(cacheKey);
  }
}
